import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { errorMessage, joinRoom, listRooms } from "../../api/client";
import { modeFromSlug, modeSlug } from "../../api/modes";
import type { RoomInfo } from "../../api/types";
import { startGame } from "../../state/game";
import { mapName, modeName, requireMeta } from "../../state/meta";
import { navigate } from "../../state/route";
import { toast, toastError } from "../../state/toasts";
import { Button } from "../components/Button";
import { TextInput } from "../components/Field";
import { ScreenFrame } from "../components/ScreenFrame";
import type { SelectOption } from "../components/Select";
import { Select } from "../components/Select";
import { Toggle } from "../components/Toggle";
import { useInterval } from "../hooks/hooks";
import { useArrowNav } from "../hooks/useArrowNav";
import { RoomPasswordModal } from "./rooms/RoomPasswordModal";
import { RoomTable } from "./rooms/RoomTable";
import type { RoomTableState } from "./rooms/RoomTable";
import {
  ALL,
  DEFAULT_ROOM_FILTERS,
  DEFAULT_ROOM_SORT,
  filterRooms,
  filtersActive,
  joinBlockReason,
  nextSort,
  sortRooms,
} from "./rooms/roomList";
import type { RoomFilters, RoomLabels, RoomSort } from "./rooms/roomList";
import { useAlive } from "./rooms/useAlive";

const REFRESH_MS = 5000;

const LABELS: RoomLabels = { modeName, mapName };

/** "Updated 3s ago". Ticks on its own so the table does not re-render every second. */
function UpdatedAgo({ at }: { at: number | null }) {
  const [now, setNow] = useState(() => Date.now());
  useInterval(() => setNow(Date.now()), 1000);
  if (at === null) return <span class="rooms-toolbar__updated">Loading{"\u2026"}</span>;
  return (
    <span class="rooms-toolbar__updated">
      Updated <span class="lcd rooms-toolbar__secs">{Math.max(0, Math.floor((now - at) / 1000))}</span>s ago
    </span>
  );
}

export function ServerBrowser() {
  const catalog = requireMeta();
  const alive = useAlive();
  const root = useRef<HTMLDivElement>(null);
  useArrowNav(root);

  const [rooms, setRooms] = useState<RoomInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState<RoomFilters>(DEFAULT_ROOM_FILTERS);
  const [sort, setSort] = useState<RoomSort>(DEFAULT_ROOM_SORT);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [passwordRoom, setPasswordRoom] = useState<RoomInfo | null>(null);

  // One request at a time. Aborting (unmount) is what makes a response stale.
  const inflight = useRef<AbortController | null>(null);

  const refresh = async (manual: boolean) => {
    if (manual) setRefreshing(true);
    if (inflight.current) return; // the running request settles `refreshing`
    const ctl = new AbortController();
    inflight.current = ctl;
    try {
      const next = await listRooms(ctl.signal);
      if (ctl.signal.aborted) return;
      setRooms(next);
      setError(null);
      setUpdatedAt(Date.now());
    } catch (e) {
      if (ctl.signal.aborted) return;
      setError(errorMessage(e));
    } finally {
      if (inflight.current === ctl) inflight.current = null;
      if (!ctl.signal.aborted) setRefreshing(false);
    }
  };

  useEffect(() => {
    void refresh(false);
    return () => {
      inflight.current?.abort();
      inflight.current = null;
    };
  }, []);
  useInterval(() => void refresh(false), REFRESH_MS);

  const visible = useMemo(
    () => sortRooms(filterRooms(rooms ?? [], filters), sort, LABELS),
    [rooms, filters, sort],
  );

  // <select> values are strings: the mode filter travels as the mode slug (or `ALL`).
  const modeOptions = useMemo<SelectOption<string>[]>(
    () => [
      { value: ALL, label: "All modes" },
      ...catalog.modes.map((m) => ({ value: modeSlug(m.mode), label: m.name })),
    ],
    [catalog],
  );
  const mapOptions = useMemo<SelectOption<string>[]>(
    () => [{ value: ALL, label: "All maps" }, ...catalog.maps.map((m) => ({ value: m.id, label: m.name }))],
    [catalog],
  );

  const join = async (room: RoomInfo) => {
    const reason = joinBlockReason(room);
    if (reason !== null) {
      toast(reason);
      return;
    }
    if (joiningId !== null) return;
    if (room.hasPassword) {
      setPasswordRoom(room);
      return;
    }
    setJoiningId(room.id);
    try {
      const result = await joinRoom(room.id);
      // The row stays busy until the game screen replaces this one.
      if (alive.current) startGame(result);
    } catch (e) {
      if (!alive.current) return;
      setJoiningId(null);
      toastError(e);
    }
  };

  const tableState: RoomTableState = rooms !== null ? "ready" : error !== null ? "failed" : "loading";
  const countText =
    rooms === null ? (
      "Loading rooms\u2026"
    ) : (
      <>
        Showing <span class="lcd rooms-browser__lcd">{visible.length}</span> of{" "}
        <span class="lcd rooms-browser__lcd">{rooms.length}</span> {rooms.length === 1 ? "room" : "rooms"}
      </>
    );

  return (
    <ScreenFrame
      title="Server browser"
      kicker="Play"
      bodyClass="rooms-browser__body"
      footer={
        <>
          <span class="rooms-browser__hint">
            <kbd>{"\u2191"}</kbd>
            <kbd>{"\u2193"}</kbd>
            <span>select a room</span>
            <kbd class="rooms-browser__hint-join">Enter</kbd>
            <span>join it</span>
          </span>
          <Button variant="primary" onClick={() => navigate("create")}>
            Create lobby
          </Button>
        </>
      }
    >
      <div class="rooms-browser" ref={root}>
        <div class="rooms-toolbar" role="search" aria-label="Filter rooms">
          <TextInput
            class="rooms-toolbar__search"
            ariaLabel="Search rooms by name or host"
            placeholder="Search name or host"
            value={filters.query}
            onValue={(query) => setFilters((f) => ({ ...f, query }))}
          />
          <Select
            class="rooms-toolbar__select"
            ariaLabel="Mode"
            value={filters.mode === ALL ? ALL : modeSlug(filters.mode)}
            options={modeOptions}
            onChange={(slug) => setFilters((f) => ({ ...f, mode: modeFromSlug(slug) ?? ALL }))}
          />
          <Select
            class="rooms-toolbar__select rooms-toolbar__select--map"
            ariaLabel="Map"
            value={filters.map}
            options={mapOptions}
            onChange={(map) => setFilters((f) => ({ ...f, map }))}
          />
          <Toggle
            class="rooms-toolbar__toggle"
            label="Hide full"
            checked={filters.hideFull}
            onChange={(hideFull) => setFilters((f) => ({ ...f, hideFull }))}
          />
          <Toggle
            class="rooms-toolbar__toggle"
            label="Hide empty"
            checked={filters.hideEmpty}
            onChange={(hideEmpty) => setFilters((f) => ({ ...f, hideEmpty }))}
          />
          <div class="rooms-toolbar__status">
            <UpdatedAgo at={updatedAt} />
            <Button size="sm" busy={refreshing} onClick={() => void refresh(true)}>
              Refresh
            </Button>
          </div>
        </div>

        <div class="rooms-browser__scroll">
          <RoomTable
            state={tableState}
            rooms={visible}
            listedCount={rooms?.length ?? 0}
            filtersActive={filtersActive(filters)}
            onClearFilters={() => setFilters(DEFAULT_ROOM_FILTERS)}
            sort={sort}
            onSort={(key) => setSort((s) => nextSort(s, key))}
            joiningId={joiningId}
            onJoin={(room) => void join(room)}
          />
        </div>

        <div class="rooms-browser__lane" aria-live="polite">
          {error !== null ? (
            <div class="notice notice--error">
              <span class="rooms-browser__notice-text">
                {rooms === null
                  ? `Could not load the room list (${error}). Retrying automatically.`
                  : `Could not refresh the room list (${error}). Showing the last known rooms.`}
              </span>
            </div>
          ) : (
            <span class="rooms-browser__count">{countText}</span>
          )}
        </div>
      </div>
      {passwordRoom && <RoomPasswordModal room={passwordRoom} onClose={() => setPasswordRoom(null)} />}
    </ScreenFrame>
  );
}
